// Shared look for the employee pages (presentation only).
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { QR } from "../../qrCode/components/qrUi";

// Uppercase grey label above a field (matches FieldTextField / FieldSelect)
export function EmpLabel({ label, required }) {
    return (
        <Typography
            component="label"
            sx={{ display: "block", mb: 0.9, fontSize: 13, fontWeight: 500, letterSpacing: 0.6, textTransform: "uppercase", color: "#4b5563" }}
        >
            {label}
            {required && (
                <Box component="span" sx={{ color: "#ef4444", ml: 0.5 }}>
                    *
                </Box>
            )}
        </Typography>
    );
}

// Plain MUI TextField restyled: 42px, rounded, 1.5px grey border, blue focus
export const empInputSx = {
    "& .MuiOutlinedInput-root": {
        height: 42,
        borderRadius: 2.5,
        bgcolor: "#fff",
        fontSize: 14,
        "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: "#e5e7eb" },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
        "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(37,99,235,0.14)" },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: QR.blue },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#ef4444" },
        "&.Mui-disabled": { bgcolor: "#f8fafc", cursor: "not-allowed" },
        "& .MuiInputBase-input.Mui-disabled": { cursor: "not-allowed" },
        "&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
        "& input::placeholder": { color: "#9ca3af", opacity: 1 },
    },
    "& .MuiInputAdornment-root .MuiIconButton-root": { color: "#64748b", "& svg": { fontSize: 19 } },
};

// Small section heading inside a form card
export function FormSection({ icon, title, subtitle, color = QR.main, children, first = false }) {
    return (
        <Box sx={{ ...(first ? {} : { mt: 3, pt: 2.5, borderTop: `1px dashed ${QR.border}` }) }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Avatar variant="rounded" sx={{ width: 28, height: 28, borderRadius: 1.5, color, bgcolor: `${color}14`, "& svg": { fontSize: 17 } }}>
                    {icon}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="#0f172a" lineHeight={1.25}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.3}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
            </Stack>
            {children}
        </Box>
    );
}

// Colour per employee type (by id, falls back to slate)
const TYPE_COLORS = ["#198754", "#2563eb", "#7c3aed", "#d97706", "#0891b2", "#db2777"];
export const empTypeColor = (id) => (id ? TYPE_COLORS[(Number(id) - 1 + TYPE_COLORS.length) % TYPE_COLORS.length] || QR.slate : QR.slate);

// Employee type chip
export function EmpTypeChip({ label, id }) {
    if (!label) return <Typography variant="body2" color="#cbd5e1">-</Typography>;
    const c = empTypeColor(id);
    return (
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, px: 1, py: 0.3, borderRadius: 5, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap", color: c, bgcolor: `${c}12`, border: `1px solid ${c}2e` }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: c }} />
            {label}
        </Box>
    );
}
